import { useTranslation } from "react-i18next";
import { Mobile } from "iconsax-react";
import { AnimatedDots, Button, Toast } from "@/shared/ui";
import { Loader } from "@/shared/components";
import { openExternalUrl } from "@/shared/utils";
import { formatRemaining } from "../hooks/egovPhase";

interface Props {
  qrCode: string | null;
  primaryLink: string | null;
  secondaryLink?: string | null;
  isBusinessLink?: boolean;
  expireAt: number | null;
  now: number;
  isCoarsePointer: boolean;
  isExpired?: boolean;
  isCancelling?: boolean;
  error?: string | null;
  /** The provider outcome is unknown — there is nothing to scan yet. */
  isPending?: boolean;
  isFinalizing?: boolean;
  canRetry?: boolean;
  onCancel: () => void;
  onRestart?: () => void;
}

export default function EgovMobileQrPanel({
  qrCode,
  primaryLink,
  secondaryLink,
  isBusinessLink = false,
  expireAt,
  now,
  isCoarsePointer,
  isExpired = false,
  isCancelling = false,
  error = null,
  isPending = false,
  isFinalizing = false,
  canRetry = true,
  onCancel,
  onRestart,
}: Props) {
  const { t } = useTranslation("Common");

  function openLink(link: string | null) {
    if (link) openExternalUrl(link);
  }

  if (isFinalizing) {
    return (
      <div className="flex flex-col items-center gap-4 radius-lg surface-component-fill p-5">
        <p className="text-body-bold-md content-base-primary text-center">
          {t("signing.finalizing.title")}
        </p>
        <p className="text-body-regular-sm content-base-secondary text-center">
          {t("signing.finalizing.text")}
          <AnimatedDots />
        </p>
        <p className="text-body-regular-sm content-base-low text-center">
          {t("signing.finalizing.hint")}
        </p>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex flex-col items-center gap-4 radius-lg surface-component-fill p-5">
        <Loader />
        <p className="text-body-bold-md content-base-primary text-center">
          {t("signing.egov.pendingTitle")}
        </p>
        <p className="text-body-regular-sm content-base-secondary text-center">
          {t("signing.egov.pendingText")}
        </p>
        {onRestart && canRetry && (
          <Button variant="primary" size="huge" className="w-full" onClick={onRestart}>
            <Mobile size={16} color="currentColor" />
            {t("signing.egov.restart")}
          </Button>
        )}
        <Button variant="plain" size="md" onClick={onCancel}>
          {t("signing.egov.close")}
        </Button>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="flex flex-col items-center gap-4 radius-lg surface-component-fill p-5">
        <p className="text-body-bold-md content-base-primary text-center">
          {t("signing.egov.expiredTitle")}
        </p>
        <p className="text-body-regular-sm content-base-secondary text-center">
          {t("signing.egov.expiredText")}
        </p>
        {onRestart && (
          <Button variant="primary" size="huge" className="w-full" onClick={onRestart}>
            <Mobile size={16} color="currentColor" />
            {t("signing.egov.restart")}
          </Button>
        )}
        <Button variant="plain" size="md" onClick={onCancel}>
          {t("signing.egov.close")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 radius-lg surface-component-fill p-5">
      {error && <Toast color="negative" text={error} closable={false} autoClose={false} isFullWidth />}

      <p className="text-body-bold-md content-base-primary text-center">
        {isCoarsePointer ? t("signing.egov.openAppTitle") : t("signing.egov.scanTitle")}
      </p>

      <p className="text-body-regular-sm content-base-secondary text-center">
        {isBusinessLink ? t("signing.egov.businessHint") : t("signing.egov.personalHint")}
      </p>

      {isCoarsePointer ? (
        <Button
          variant="primary"
          size="huge"
          className="w-full"
          onClick={() => openLink(primaryLink)}>
          <Mobile size={16} color="currentColor" />
          {isBusinessLink ? t("signing.egov.openBusinessApp") : t("signing.egov.openApp")}
        </Button>
      ) : (
        qrCode && (
          <img
            src={`data:image/png;base64,${qrCode}`}
            alt={t("signing.egov.qrAlt")}
            className="w-64 h-64 radius-md surface-base-fill"
          />
        )
      )}

      {secondaryLink && isCoarsePointer && (
        <Button variant="plain" size="md" onClick={() => openLink(secondaryLink)}>
          {isBusinessLink ? t("signing.egov.openApp") : t("signing.egov.openBusinessApp")}
        </Button>
      )}

      {expireAt && (
        <p className="text-body-regular-sm content-base-secondary pt-4">
          {t("signing.egov.expiresIn", { time: formatRemaining(expireAt, now) })}
        </p>
      )}

      <Button variant="danger" size="md" disabled={isCancelling} onClick={onCancel}>
        {t("signing.egov.cancel")}
      </Button>
    </div>
  );
}
